<template>
  <div class="stats-container">
    <Loader v-if="loader" />
    
    <h5>Seleziona una Pratica</h5>
    <div class="gap-4">
      <Button 
        v-for="tipo in tipiPratiche" 
        :key="tipo.value"
        :class="{ 'p-button-outlined': selectedPratica !== tipo.value }"
        @click="selectPratica(tipo.value)"
        class="p-button-rounded mr-2 mb-2"
      >
        {{ tipo.label }}
      </Button>
    <div class="grid">
      <div class="col-12">
        <div class="card">
          <h5>Statistiche Generali - Totali per Pratica</h5>
          <Chart type="bar" :data="totaliGeneraliData" :options="chartOptions" class="h-20rem" />
        </div>
      </div>
    </div>
    </div>

    <div v-if="incallStats" class="grid">
      <div class="col-12">
        <div class="card">
          <h5>Pratiche aperte da Incall</h5>
          <div class="incall-stats-summary">
            <button
              type="button"
              class="incall-stat-box"
              :class="{ 'is-active': incallStatusFilter === null }"
              @click="incallStatusFilter = null"
            >
              <span class="incall-stat-value">{{ incallStats.totale || 0 }}</span>
              <span class="incall-stat-label">Totale</span>
            </button>
            <button
              type="button"
              class="incall-stat-box is-waiting"
              :class="{ 'is-active': incallStatusFilter === 'waiting_sign' }"
              @click="incallStatusFilter = 'waiting_sign'"
            >
              <span class="incall-stat-value">{{ incallStats.by_status?.waiting_sign || 0 }}</span>
              <span class="incall-stat-label">In attesa firma</span>
            </button>
            <button
              type="button"
              class="incall-stat-box is-pending"
              :class="{ 'is-active': incallStatusFilter === 'pending' }"
              @click="incallStatusFilter = 'pending'"
            >
              <span class="incall-stat-value">{{ incallStats.by_status?.pending || 0 }}</span>
              <span class="incall-stat-label">Da confermare</span>
            </button>
            <button
              type="button"
              class="incall-stat-box is-confirmed"
              :class="{ 'is-active': incallStatusFilter === 'confirmed' }"
              @click="incallStatusFilter = 'confirmed'"
            >
              <span class="incall-stat-value">{{ incallStats.by_status?.confirmed || 0 }}</span>
              <span class="incall-stat-label">Confermate</span>
            </button>
          </div>
          <div class="incall-status-legend">
            <p><b>In attesa firma:</b> OTP inviato, il cliente non ha ancora firmato.</p>
            <p><b>Da confermare:</b> firmata, visibile nel tab “Da confermare” finché non viene inserita in gestione.</p>
            <p><b>Confermate:</b> già inserite nella tabella reale (Cessazione, MC1, Verifica, Spostamento).</p>
          </div>
          <DataTable
            :value="filteredIncallItems"
            paginator
            :rows="20"
            :rowsPerPageOptions="[10, 20, 50, 100]"
            dataKey="id"
            sortField="created_at"
            :sortOrder="-1"
            scrollable
            scrollHeight="420px"
            emptyMessage="Nessuna pratica Incall per questo stato"
            responsiveLayout="scroll"
            class="incall-stats-table"
          >
            <Column field="status" header="Stato" :sortable="true" style="min-width: 150px">
              <template #body="{ data }">
                <span class="incall-status-badge" :class="`is-${data.status}`">
                  {{ incallStatusLabel(data.status) }}
                </span>
              </template>
            </Column>
            <Column field="tipo_label" header="Tipologia" :sortable="true" style="min-width: 140px"></Column>
            <Column field="code" header="POD / PDR" :sortable="true" style="min-width: 130px">
              <template #body="{ data }">{{ data.code || '-' }}</template>
            </Column>
            <Column field="cliente_nome" header="Cliente" :sortable="true" style="min-width: 160px">
              <template #body="{ data }">{{ data.cliente_nome || data.nominativo || '-' }}</template>
            </Column>
            <Column field="created_at" header="Aperta il" :sortable="true" style="min-width: 150px">
              <template #body="{ data }">{{ formatIncallDate(data.created_at) }}</template>
            </Column>
            <Column field="signed_at" header="Firmata il" :sortable="true" style="min-width: 150px">
              <template #body="{ data }">{{ formatIncallDate(data.signed_at) }}</template>
            </Column>
            <Column field="confirmed_at" header="Confermata il" :sortable="true" style="min-width: 150px">
              <template #body="{ data }">{{ formatIncallDate(data.confirmed_at) }}</template>
            </Column>
            <Column field="created_by" header="Aperta da" :sortable="true" style="min-width: 130px"></Column>
          </DataTable>
          <Chart type="bar" :data="incallByTipoData" :options="chartOptions" class="h-20rem mt-4" />
        </div>
      </div>
      <div v-if="incallMonthlyChartData" class="col-12">
        <div class="card">
          <h5>Andamento mensile - Pratiche da Incall</h5>
          <Chart type="bar" :data="incallMonthlyChartData" :options="monthlyChartOptions" class="h-20rem" />
        </div>
      </div>
    </div>
    
    <div v-if="selectedPratica && statsData" class="grid">
      <div class="col-12">
        <div class="card">
          <h5>Statistiche Totali - {{ getPraticaLabel(selectedPratica) }}</h5>
          <Chart type="bar" :data="chartData" :options="chartOptions" class="h-20rem" />
        </div>
      </div>
      
      <div class="col-12">
        <div class="card">
          <h5>Statistiche Mensili - {{ getPraticaLabel(selectedPratica) }}</h5>
          <Chart type="line" :data="monthlyChartData" :options="monthlyChartOptions" class="h-20rem" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from "vue";
import Service from "@/services/ask-pratiche/gestione-pratiche";
import Loader from "@/components/Loader.vue";
import Chart from 'primevue/chart';

export default {
  components: {
    Loader,
    Chart
  },
  setup() {
    const loader = ref(true);
    const service = new Service();
    const statsData = ref(null);
    const incallStats = ref(null);
    const incallStatusFilter = ref(null);
    const selectedPratica = ref(null);

    const tipiPratiche = [
      { label: 'Cessazione', value: 'cessazione' },
      { label: 'MC1', value: 'mc1' },
      { label: 'Posa Contatore', value: 'posa_contatore' },
      { label: 'Spostamento Contatore', value: 'spostamento_contatore' },
      { label: 'Voltura', value: 'voltura' },
      { label: 'Subentro', value: 'subentro' },
      { label: 'VT4', value: 'vt4' }
    ];

    const getPraticaLabel = (value) => {
      return tipiPratiche.find(tipo => tipo.value === value)?.label || value;
    };

    const chartData = computed(() => {
      if (!statsData.value || !selectedPratica.value) return null;
      
      const stats = statsData.value.totali[selectedPratica.value];
      return {
        labels: ['Totale', 'Completate', 'In Corso', 'Senza Stato'],
        datasets: [{
          label: getPraticaLabel(selectedPratica.value),
          data: [stats.totale, stats.completate, stats.in_corso, stats.senza_stato],
          backgroundColor: [
            '#42A5F5',
            '#66BB6A',
            '#FFA726',
            '#EC407A'
          ]
        }]
      };
    });

    const monthlyChartData = computed(() => {
      if (!statsData.value || !selectedPratica.value) return null;

      const monthlyStats = statsData.value.mensili;
      const months = Object.keys(monthlyStats).sort();
      
      return {
        labels: months,
        datasets: [
          {
            type: 'bar',
            label: 'Totale',
            data: months.map(month => monthlyStats[month][selectedPratica.value].totale),
            backgroundColor: '#42A5F5'
          },
          {
            type: 'bar',
            label: 'Completate',
            data: months.map(month => monthlyStats[month][selectedPratica.value].completate),
            backgroundColor: '#66BB6A'
          },
          {
            type: 'bar',
            label: 'In Corso',
            data: months.map(month => monthlyStats[month][selectedPratica.value].in_corso),
            backgroundColor: '#FFA726'
          },
          {
            type: 'bar',
            label: 'Senza Stato',
            data: months.map(month => monthlyStats[month][selectedPratica.value].senza_stato),
            backgroundColor: '#EC407A'
          }
        ]
      };
    });

    const chartOptions = {
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true
        }
      },
      plugins: {
        legend: {
          position: 'bottom',
          padding: 20
        }
      },
      responsive: true,
      barPercentage: 0.7
    };

    const monthlyChartOptions = {
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true
        },
        x: {
          stacked: false
        }
      },
      plugins: {
        legend: {
          position: 'bottom',
          padding: 20
        }
      },
      responsive: true,
      barPercentage: 0.7
    };

    const selectPratica = (pratica) => {
      selectedPratica.value = pratica;
    };

    const totaliGeneraliData = computed(() => {
      if (!statsData.value) return null;
      
      const labels = tipiPratiche.map(tipo => tipo.label);
      const data = tipiPratiche.map(tipo => 
        statsData.value.totali[tipo.value]?.totale || 0
      );

      return {
        labels: labels,
        datasets: [{
          label: 'Totale Pratiche',
          data: data,
          backgroundColor: [
            '#42A5F5',  
            '#66BB6A',  
            '#FFA726',  
            '#EC407A',  
            '#AB47BC',  
            '#7E57C2',  
            '#26C6DA'   
          ]
        }]
      };
    });

    const incallTipoLabels = [
      { label: 'Cessazione', value: 'cessazione' },
      { label: 'MC1', value: 'mc1' },
      { label: 'Verifica', value: 'verifica' },
      { label: 'Spostamento', value: 'spostamento' },
    ];

    const incallByTipoData = computed(() => {
      if (!incallStats.value) return null;

      return {
        labels: incallTipoLabels.map((tipo) => tipo.label),
        datasets: [{
          label: 'Aperte da Incall',
          data: incallTipoLabels.map((tipo) => incallStats.value.by_tipo?.[tipo.value] || 0),
          backgroundColor: ['#42A5F5', '#66BB6A', '#FFA726', '#7E57C2'],
        }],
      };
    });

    const incallMonthlyChartData = computed(() => {
      if (!incallStats.value?.mensili) return null;

      const months = Object.keys(incallStats.value.mensili).sort();
      if (!months.length) return null;

      return {
        labels: months,
        datasets: [
          {
            label: 'In attesa firma',
            data: months.map((month) => incallStats.value.mensili[month].waiting_sign || 0),
            backgroundColor: '#FFA726',
          },
          {
            label: 'Da confermare',
            data: months.map((month) => incallStats.value.mensili[month].pending || 0),
            backgroundColor: '#42A5F5',
          },
          {
            label: 'Confermate',
            data: months.map((month) => incallStats.value.mensili[month].confirmed || 0),
            backgroundColor: '#66BB6A',
          },
        ],
      };
    });

    const filteredIncallItems = computed(() => {
      const items = Array.isArray(incallStats.value?.items) ? incallStats.value.items : [];
      if (!incallStatusFilter.value) return items;
      return items.filter((item) => item.status === incallStatusFilter.value);
    });

    const incallStatusLabel = (status) => {
      return {
        waiting_sign: 'In attesa firma',
        pending: 'Da confermare',
        confirmed: 'Confermata',
        rejected: 'Scartata',
        lavorata: 'Lavorata',
      }[status] || status;
    };

    const formatIncallDate = (value) => {
      if (!value) return '-';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '-';
      const dd = String(date.getDate()).padStart(2, '0');
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const yyyy = date.getFullYear();
      const hh = String(date.getHours()).padStart(2, '0');
      const min = String(date.getMinutes()).padStart(2, '0');
      return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
    };

    const fetchPraticheStats = async () => {
      try {
        const [response, incall] = await Promise.all([
          service.getPraticheStats(),
          service.getPraticheStalloIncallStats(),
        ]);
        statsData.value = response.stats;
        incallStats.value = incall;
      } catch (error) {
        console.error("Errore durante il recupero delle statistiche:", error);
      } finally {
        loader.value = false;
      }
    };

    onMounted(() => {
      fetchPraticheStats();
    });

    return {
      loader,
      chartData,
      chartOptions,
      monthlyChartData,
      monthlyChartOptions,
      tipiPratiche,
      selectedPratica,
      selectPratica,
      getPraticaLabel,
      statsData,
      totaliGeneraliData,
      incallStats,
      incallStatusFilter,
      incallByTipoData,
      incallMonthlyChartData,
      filteredIncallItems,
      incallStatusLabel,
      formatIncallDate,
    };
  }
};
</script>

<style scoped>
.stats-container {
  padding: 20px;
}

.card {
  background: var(--surface-card);
  padding: 1.5rem;
  margin-bottom: 1rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  height: auto;
}

.card h5 {
  margin-top: 0;
  margin-bottom: 1rem;
  color: var(--text-color);
}

.h-20rem {
  height: 20rem !important;
  margin-bottom: 2rem;
}

.mb-5 {
  margin-bottom: 3rem !important;
}

.incall-stats-summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}

.incall-stat-box {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.85rem 1rem;
  border-radius: 10px;
  background: #eff6ff;
  border: 1px solid #dbeafe;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
}

.incall-stat-box.is-waiting {
  background: #fff7ed;
  border-color: #ffedd5;
}

.incall-stat-box.is-pending {
  background: #eff6ff;
  border-color: #dbeafe;
}

.incall-stat-box.is-confirmed {
  background: #f0fdf4;
  border-color: #dcfce7;
}

.incall-stat-box.is-active {
  box-shadow: 0 0 0 2px #0d9488;
}

.incall-status-legend {
  margin: 0 0 1rem;
  padding: 0.75rem 0.9rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.incall-status-legend p {
  margin: 0 0 0.3rem;
  font-size: 0.82rem;
  color: #475569;
  line-height: 1.4;
}

.incall-status-legend p:last-child {
  margin-bottom: 0;
}

.incall-status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
}

.incall-status-badge.is-waiting_sign {
  background: #ffedd5;
  color: #9a3412;
}

.incall-status-badge.is-pending {
  background: #dbeafe;
  color: #1e40af;
}

.incall-status-badge.is-confirmed {
  background: #dcfce7;
  color: #166534;
}

.incall-status-badge.is-rejected {
  background: #fee2e2;
  color: #991b1b;
}

.incall-status-badge.is-lavorata {
  background: #e2e8f0;
  color: #334155;
}

.incall-stats-table {
  margin-bottom: 0.5rem;
}

.incall-stats-table :deep(.p-datatable-wrapper) {
  max-height: 420px;
}

.incall-stats-table :deep(.p-datatable-thead > tr > th) {
  position: sticky;
  top: 0;
  z-index: 1;
  background: var(--surface-card, #fff);
}

.incall-stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.1;
  color: #0f172a;
}

.incall-stat-label {
  font-size: 0.8rem;
  color: #64748b;
}

@media (max-width: 768px) {
  .incall-stats-summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
